import React, { useEffect, useState } from 'react';
import {
  GlobalOutlined,
  PictureFilled,
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  ExportOutlined,
  CopyOutlined,
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  LoadingOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useWebsiteCardController } from './useWebsiteCardController';
import { AssetVersionItemDto } from '@api/Api';
import './WebsiteCard.less';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';

interface WebsiteCardProps {
  assetId: string;
  assetVersion: AssetVersionItemDto & { metadata: { type: 'website' } };
  isDesktopSidebarOpen?: boolean;
  viewerType: 'main' | 'left' | 'right';
}

function WebsiteCard({
  assetId,
  assetVersion,
  isDesktopSidebarOpen = false,
  viewerType
}: WebsiteCardProps) {
  const uppy = useMVCommentAttachUppy(assetId, assetVersion.id);
  const {
    frameElement,
    faviconUrl,
    clearFavicon,
    currentUrl,
    viewport,
    viewportAvailability,
    setViewport,
    canBack,
    canForward,
    isLoading,
    loadingButton,
    back,
    forward,
    reload,
    hardReload,
    openInNewTab,
    copyUrl,
    snapshot,
    takingSnapshot,
    cancelSnapshot
  } = useWebsiteCardController({
    assetVersion,
    uppy,
    viewerType
  });

  // Right-click on Reload opens a small menu with the "Empty cache and
  // hard reload" action — modeled on Chromium's reload-button context
  // menu. We track screen coordinates rather than anchoring to the
  // button so the menu can sit just below the cursor.
  const [reloadMenu, setReloadMenu] = useState<{ x: number; y: number } | null>(
    null
  );
  useEffect(() => {
    if (!reloadMenu) return undefined;
    const close = () => setReloadMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // Close on any click anywhere, on Escape, and on blur so the menu
    // doesn't get stranded when focus leaves the window.
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', close);
    };
  }, [reloadMenu]);
  const handleReloadContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setReloadMenu({ x: e.clientX, y: e.clientY });
  };
  const handleResetAndReload = () => {
    setReloadMenu(null);
    hardReload();
  };

  const showControls = !snapshot && !takingSnapshot;
  const unavailableViewportTooltip = (viewportName: string) => (
    <div className="media-viewer-tooltip-inner" style={{ padding: 8 }}>
      {`Not enough screen space for the ${viewportName} viewport.`}
      {isDesktopSidebarOpen && ' Close the sidebar to free up space.'}
    </div>
  );

  return (
    <div className="media_viewer_website">
      <div className="media_viewer_website__url_bar">
        {showControls && (
          <>
            <GlobalOutlined className="media_viewer_website__url_bar_icon" />
            <div className="media_viewer_website__nav">
              <button
                type="button"
                className="media_viewer_website__icon_btn"
                tabIndex={-1}
                onClick={back}
                disabled={!canBack}
                title="Back"
              >
                {isLoading && loadingButton === 'back' ? (
                  <LoadingOutlined />
                ) : (
                  <LeftOutlined />
                )}
              </button>
              <button
                type="button"
                className="media_viewer_website__icon_btn"
                tabIndex={-1}
                onClick={forward}
                disabled={!canForward}
                title="Forward"
              >
                {isLoading && loadingButton === 'forward' ? (
                  <LoadingOutlined />
                ) : (
                  <RightOutlined />
                )}
              </button>
              <button
                type="button"
                className="media_viewer_website__icon_btn"
                tabIndex={-1}
                onClick={reload}
                onContextMenu={handleReloadContextMenu}
                title="Reload (right-click for more)"
              >
                {isLoading &&
                (loadingButton === 'reload' || loadingButton === null) ? (
                  <LoadingOutlined />
                ) : (
                  <ReloadOutlined />
                )}
              </button>
              <button
                type="button"
                className="media_viewer_website__icon_btn"
                tabIndex={-1}
                onClick={openInNewTab}
                title="Open in new tab"
              >
                <ExportOutlined />
              </button>
            </div>
            <div className="media_viewer_website__url_field">
              {!!faviconUrl && (
                <img
                  className="media_viewer_website__favicon"
                  src={faviconUrl}
                  alt=""
                  // Favicons are fetched from the upstream origin directly
                  // (the bridge sends an upstream URL). If the upstream
                  // 4xxs the icon, hide the broken-image glyph silently.
                  onError={clearFavicon}
                />
              )}
              <span
                className="media_viewer_website__url_text"
                title={currentUrl}
              >
                {currentUrl}
              </span>
              <button
                type="button"
                className="media_viewer_website__icon_btn"
                tabIndex={-1}
                onClick={copyUrl}
                title="Copy URL"
              >
                <CopyOutlined />
              </button>
            </div>
            <Tooltip
              title={
                viewportAvailability.desktop
                  ? undefined
                  : unavailableViewportTooltip('Desktop')
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="bottom"
              overlayClassName="toggle_favorite_overlay"
            >
              <span className="media_viewer_website__viewport_btn_wrapper">
                <button
                  type="button"
                  className={`media_viewer_website__icon_btn${
                    viewport === 'desktop'
                      ? ' media_viewer_website__icon_btn--active'
                      : ''
                  }`}
                  tabIndex={-1}
                  onClick={() => setViewport('desktop')}
                  disabled={!viewportAvailability.desktop}
                  title={viewportAvailability.desktop ? 'Desktop' : undefined}
                  aria-label="Desktop viewport"
                >
                  <DesktopOutlined />
                </button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                viewportAvailability.tablet
                  ? undefined
                  : unavailableViewportTooltip('Tablet')
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="bottom"
              overlayClassName="toggle_favorite_overlay"
            >
              <span className="media_viewer_website__viewport_btn_wrapper">
                <button
                  type="button"
                  className={`media_viewer_website__icon_btn${
                    viewport === 'tablet'
                      ? ' media_viewer_website__icon_btn--active'
                      : ''
                  }`}
                  tabIndex={-1}
                  onClick={() => setViewport('tablet')}
                  disabled={!viewportAvailability.tablet}
                  title={viewportAvailability.tablet ? 'Tablet' : undefined}
                  aria-label="Tablet viewport"
                >
                  <TabletOutlined />
                </button>
              </span>
            </Tooltip>
            <button
              type="button"
              className={`media_viewer_website__icon_btn${
                viewport === 'mobile'
                  ? ' media_viewer_website__icon_btn--active'
                  : ''
              }`}
              tabIndex={-1}
              onClick={() => setViewport('mobile')}
              title="Mobile"
            >
              <MobileOutlined />
            </button>
          </>
        )}
        {!showControls && (
          <>
            {takingSnapshot && (
              <>
                <LoadingOutlined className="media_viewer_website__url_bar_icon" />
                <span className="media_viewer_website__snapshot_title">
                  Taking Snapshot...
                </span>
              </>
            )}
            {snapshot && (
              <>
                <PictureFilled className="media_viewer_website__url_bar_icon" />
                <span className="media_viewer_website__snapshot_title">
                  Snapshot
                </span>
              </>
            )}

            <span
              className="media_viewer_website__snapshot_url_text"
              title={currentUrl}
            >
              {currentUrl}
            </span>
            <button
              type="button"
              className="media_viewer_website__icon_btn"
              tabIndex={-1}
              title="Close snapshot"
              onClick={cancelSnapshot}
            >
              <CloseOutlined />
            </button>
          </>
        )}
      </div>
      {frameElement}
      {reloadMenu && (
        <ul
          className="media_viewer_website__context_menu"
          style={{ left: reloadMenu.x, top: reloadMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <li>
            <button
              type="button"
              className="media_viewer_website__context_menu_item"
              onClick={handleResetAndReload}
            >
              Empty Cache and Hard Reload
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export default WebsiteCard;
